import { useMemo } from 'react'
import { ModalId, PDFTabContent, useUIActions } from '../../../../../features/ui'
import { useDataTypesHooks, useDescribeHooks } from '../../../../../features/data-types'
import { TableName } from '../../../../../tables'
import { DocumentSection, DocumentSectionListPanel } from '../../../../../features/files'
import { Delivery, deliveryHelpers, DeliveryMethod, DeliveryOrder } from '../../../../deliveries'
import { toDate } from '../../../../../utils/typeUtils'
import { PurchaseOrder } from '../../../state/types'
import { PurchasePDF } from '../purchase-pdf/purchasePDF'
import { DownloadPurchasePDFModal } from '../download-purchase-pdf-modal/downloadPurchasePDFModal'
import { Ticket, ticketHelpers } from '../../../../administration'

type Props = {
    purchase: PurchaseOrder
    deliveries: Delivery[]
    deliveryOrders: DeliveryOrder[]
    tickets: Ticket[]
    onDownloadTicket: (ticket: Ticket) => void
    onDownloadDeliveryNote: (delivery: Delivery) => void
}

export const PurchaseDocumentsPanel = (props: Props) => {
    const { purchase, deliveries, deliveryOrders, tickets, onDownloadTicket, onDownloadDeliveryNote } = props

    const { describeTicket } = ticketHelpers
    const { describeDelivery } = deliveryHelpers
    const { describe } = useDescribeHooks()
    const { formatDate } = useDataTypesHooks()

    const { openModal } = useUIActions()
    
    const findDeliveryOrder = (delivery: Delivery) => deliveryOrders.find(order => order.id === delivery.orderId)

    const onDownloadOrder = () => openModal(ModalId.DOWNLOAD_PURCHASE_PDF)

    const handleDownloadTicket = (ticket: Ticket) => {
        onDownloadTicket(ticket)
        openModal(ModalId.DOWNLOAD_TICKET)
    }

    const handleDownloadDeliveryNote = (delivery: Delivery) => {
        onDownloadDeliveryNote(delivery)
        openModal(ModalId.DOWNLOAD_DELIVERY_NOTE)
    }

    const downloadAction = (onClick: () => void) => ({
        icon: 'download',
        label: 'Descargar',
        onClick
    })

    const sections = useMemo((): DocumentSection[] => {
        const orderId = 'order'
        const sectionList: DocumentSection[] = [
            {
                id: `${orderId}-section`,
                title: `Orden de ${describe(TableName.PURCHASE_ORDERS, purchase)}`,
                content: (
                    <PDFTabContent>
                        <PurchasePDF
                            id={orderId}
                            purchase={purchase}
                            previewMode
                        />
                    </PDFTabContent>
                ),
                headerActions: [downloadAction(onDownloadOrder)]
            }
        ]

        tickets
            .filter(ticket => !!ticket.ticketUrl)
            .forEach(ticket => {
                sectionList.push({
                    id: `ticket-${ticket.id}-section`,
                    title: `${describeTicket(ticket)} - ${formatDate(toDate(ticket.creationDate))}`,
                    headerActions: [downloadAction(() => handleDownloadTicket(ticket))]
                })
            })

        deliveries.forEach(delivery => {
            const { method, deliveryNoteUrl} = delivery
            if (method !== DeliveryMethod.RECEPTION || deliveryNoteUrl) {
                const deliveryOrder = findDeliveryOrder(delivery)
                sectionList.push({
                    id: `delivery-${delivery.id}-section`,
                    title: `${describeDelivery(delivery, deliveryOrder, true)} - ${formatDate(toDate(delivery.date))}`,
                    headerActions: [downloadAction(() => handleDownloadDeliveryNote(delivery))]
                })
            }
        })

        return sectionList
    }, [purchase.id, deliveries, deliveryOrders, tickets, onDownloadTicket, onDownloadDeliveryNote])

    return (
        <>
            <DocumentSectionListPanel sections={sections} />
            <DownloadPurchasePDFModal purchase={purchase} />
        </>
    )
}
